'use strict';

var debounce = require('lodash/debounce');

var SEARCH_DELAY_MS = 500;
var MIN_CHARS = 3;
var SUGGESTION_COUNT = 6;
var INPUT_SELECTOR = '.js-search-bar';
var PANEL_SELECTOR = '.nav-search';
var STATES = ['first-search', 'no-search', 'search-typed', 'no-results', 'search-loading'];
var CLEARED_SEARCHES_COOKIE = 'navSearchCleared';
var CLEARED_SEARCHES_MAX_AGE = 60 * 60 * 24 * 365;

var panelRequest = null;
var mobileSearchQuery = window.matchMedia('(max-width: 991.98px)');
var SEARCH_MOTION_MS = 300;
var searchCloseTimer = null;
var pageWaitReasons = {};
var slideWaitId = 0;
var slideWaitTimer = null;
var slideWaitCleanup = null;
var suggestionRequest = null;
var productsRequest = null;
var searchSerial = 0;
var noResultQuery = '';
var loadedQuery = '';
var completionByInput = new WeakMap();
var tokenByInput = new WeakMap();

/**
 * @returns {string[]} Phrases the shopper removed from recent searches
 */
function readClearedPhrases() {
    var parts = document.cookie ? document.cookie.split(';') : [];
    var value = '';
    var part;
    var i;

    for (i = 0; i < parts.length; i++) {
        part = parts[i].replace(/^\s+/, '');

        if (part.indexOf(CLEARED_SEARCHES_COOKIE + '=') === 0) {
            value = part.substring(CLEARED_SEARCHES_COOKIE.length + 1);
            break;
        }
    }

    if (!value) {
        return [];
    }

    return value.split('|').map(function (part) {
        try {
            return decodeURIComponent(part.replace(/\+/g, ' '));
        } catch (error) {
            return '';
        }
    }).filter(Boolean);
}

/**
 * @param {string[]} names - Phrases that stay hidden
 */
function writeClearedPhrases(names) {
    var value = names.map(encodeURIComponent).join('|');
    var maxAge = names.length ? CLEARED_SEARCHES_MAX_AGE : 0;

    document.cookie = CLEARED_SEARCHES_COOKIE + '=' + value
        + '; path=/; max-age=' + maxAge + '; SameSite=Lax';
}

/**
 * Lets a phrase show again after the shopper searches for it.
 *
 * @param {string} name - Search text
 */
function releaseClearedPhrase(name) {
    var typed = (name || '').trim().toLowerCase();

    if (!typed) {
        return;
    }

    writeClearedPhrases(readClearedPhrases().filter(function (phrase) {
        return phrase.toLowerCase() !== typed;
    }));
}

/**
 * @param {string} href - Link that may carry a q parameter
 * @returns {string} Decoded query, or an empty string
 */
function queryFromHref(href) {
    var match = String(href || '').match(/[?&]q=([^&#]*)/);

    if (!match) {
        return '';
    }

    try {
        return decodeURIComponent(match[1].replace(/\+/g, ' '));
    } catch (error) {
        return '';
    }
}

/**
 * Hides the current recent searches and returns the panel to the first-visit state.
 *
 * @param {HTMLButtonElement} button - Clear control in the recent-search block
 */
function clearLastSearches(button) {
    var block = button.closest('.nav-search__you-last-searches');
    var names = readClearedPhrases();
    var seen = {};
    var input;

    if (!block) {
        return;
    }

    names.forEach(function (phrase) {
        seen[phrase.toLowerCase()] = true;
    });

    block.querySelectorAll('.nav-search__you-last-searches-link').forEach(function (link) {
        var phrase = (link.textContent || '').trim();

        if (phrase && !seen[phrase.toLowerCase()]) {
            seen[phrase.toLowerCase()] = true;
            names.push(phrase);
        }

        link.remove();
    });

    writeClearedPhrases(names);
    $(PANEL_SELECTOR).attr('data-has-recent-searches', 'false');

    input = document.activeElement;

    if (!isSearchInput(input)) {
        input = document.querySelector(INPUT_SELECTOR);
    }

    if (input) {
        applyState(currentState(input));
    }
}

/**
 * @param {EventTarget} node - Event target
 * @returns {boolean} True when the node is a header search field
 */
function isSearchInput(node) {
    return !!(node && typeof node.matches === 'function' && node.matches(INPUT_SELECTOR));
}

/**
 * @param {EventTarget} node - Focused node
 * @returns {boolean} True when focus is still in the search field or its panel
 */
function isSearchUi(node) {
    if (!node || typeof node.closest !== 'function') {
        return false;
    }

    if (node.closest('.js-nav-search-close')) {
        return false;
    }

    return !!node.closest('.nav-bar__search, .nav-search');
}

/**
 * @param {string} url - Endpoint that already may contain a query string
 * @param {string} query - Search text
 * @returns {string} URL with q appended
 */
function withQuery(url, query) {
    var joiner = url.indexOf('?') === -1 ? '?' : '&';
    return url + joiner + 'q=' + encodeURIComponent(query);
}

/**
 * @param {JQuery.jqXHR} request - In-flight request
 */
function abortRequest(request) {
    if (request && request.abort) {
        request.abort();
    }
}

/**
 * Drops suggestion and product requests that are still in flight.
 */
function abortSearches() {
    abortRequest(suggestionRequest);
    abortRequest(productsRequest);
    suggestionRequest = null;
    productsRequest = null;
}

/**
 * @param {Element} element - Node that declares state classes
 * @param {string} classNames - Space-separated class list
 * @param {boolean} present - Whether those classes should be on the node
 */
function setClassToken(element, classNames, present) {
    if (!classNames) {
        return;
    }

    classNames.split(/\s+/).forEach(function (token) {
        if (token) {
            element.classList.toggle(token, present);
        }
    });
}

/**
 * Shows a block when the current state is one it declares.
 * A block can declare several states; the others hide it.
 *
 * @param {string} state - first-search, no-search, search-typed, no-results, or search-loading
 */
function applyState(state) {
    $('.navigation').find('.js-nav-search-input-display-parts, .js-nav-input-typed').each(function () {
        var element = this;
        var rules = [];
        var matched = null;

        STATES.forEach(function (name) {
            var add = element.getAttribute('data-' + name + '-add-class')
                || element.getAttribute('data-' + name + '-add');
            var remove = element.getAttribute('data-' + name + '-remove-class')
                || element.getAttribute('data-' + name + '-rm');

            if (!add && !remove) {
                return;
            }

            var rule = {
                add: add,
                remove: remove
            };

            rules.push(rule);

            if (name === state) {
                matched = rule;
            }
        });

        if (!rules.length) {
            return;
        }

        if (!matched) {
            matched = {
                add: rules[0].remove,
                remove: rules[0].add
            };
        }

        setClassToken(element, matched.add, true);
        setClassToken(element, matched.remove, false);
    });
}

/**
 * @param {HTMLInputElement} input - Search field that fired the event
 * @returns {string} Panel state for the current value
 */
function currentState(input) {
    var hasRecent = $(PANEL_SELECTOR).attr('data-has-recent-searches') === 'true';
    var query = input.value.trim();

    if (query.length >= MIN_CHARS) {
        if (query === noResultQuery) {
            return 'no-results';
        }

        return query === loadedQuery ? 'search-typed' : 'search-loading';
    }

    return hasRecent ? 'no-search' : 'first-search';
}

/**
 * @param {string} reason - Why the page cursor is waiting
 * @param {boolean} on - Whether that reason is active
 */
function setPageWait(reason, on) {
    if (on) {
        pageWaitReasons[reason] = true;
    } else {
        delete pageWaitReasons[reason];
    }

    document.body.classList.toggle('nav-search-wait', Object.keys(pageWaitReasons).length > 0);
}

/**
 * Drops the wait cursor tied to the panel slide.
 */
function clearSlideWait() {
    slideWaitId += 1;
    window.clearTimeout(slideWaitTimer);
    slideWaitTimer = null;

    if (slideWaitCleanup) {
        slideWaitCleanup();
        slideWaitCleanup = null;
    }

    setPageWait('slide', false);
}

/**
 * Holds the page cursor until the panel has finished sliding open.
 *
 * @param {HTMLElement} panel - Search panel
 */
function holdPageWaitForSlide(panel) {
    var waitId;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    clearSlideWait();
    waitId = slideWaitId;
    setPageWait('slide', true);

    function finish(event) {
        if (waitId !== slideWaitId) {
            return;
        }

        if (event && (event.target !== panel || event.propertyName !== 'transform')) {
            return;
        }

        clearSlideWait();
    }

    slideWaitCleanup = function () {
        panel.removeEventListener('transitionend', finish);
    };
    panel.addEventListener('transitionend', finish);
    slideWaitTimer = window.setTimeout(finish, SEARCH_MOTION_MS + 50);
}

/**
 * @returns {JQuery.Promise} Resolves with the panel once it is in the page
 */
function ensurePanel() {
    var $panel = $(PANEL_SELECTOR);
    var url;

    if ($panel.length) {
        return $.Deferred().resolve($panel).promise();
    }

    if (panelRequest) {
        return panelRequest;
    }

    url = $('.navigation').attr('data-nav-search-url');

    if (!url) {
        return $.Deferred().reject().promise();
    }

    setPageWait('panel', true);
    panelRequest = $.ajax({
        url: url,
        method: 'GET',
        cache: false
    }).done(function (html) {
        var $navigation = $('.navigation');

        if ($navigation.find(PANEL_SELECTOR).length) {
            return;
        }

        var $slot = $navigation.children('.nav-search-slot');

        if ($slot.length) {
            $slot.append(html);
        } else {
            $navigation.append(html);
        }
    }).fail(function () {
        panelRequest = null;
    }).always(function () {
        setPageWait('panel', false);
    });

    return panelRequest;
}

/**
 * @param {JQuery} $template - Hidden row kept in the listing for cloning
 */
function resetTemplate($template) {
    $template.addClass('d-none').removeClass('d-flex').attr('href', '#');
    $template.find('span').text('');
}

/**
 * Keeps named rows, capped at one full suggestion list.
 *
 * @param {Array<{name: string, url: string}>} items - Rows from one suggestion type
 * @returns {Array<{name: string, url: string}>} Usable rows
 */
function namedSuggestions(items) {
    return (items || []).filter(function (item) {
        return item && String(item.name || '').trim();
    }).slice(0, SUGGESTION_COUNT);
}

/**
 * Fills six suggestion slots, split evenly when both types can supply them.
 *
 * @param {Array<{name: string, url: string}>} categories - Category matches
 * @param {Array<{name: string, url: string}>} contents - Content matches
 * @returns {Object} Rows to render, under categories and contents
 */
function balanceSuggestions(categories, contents) {
    var categoryRows = namedSuggestions(categories);
    var contentRows = namedSuggestions(contents);
    var half = Math.floor(SUGGESTION_COUNT / 2);
    var categoryCount;
    var contentCount;

    if (categoryRows.length + contentRows.length <= SUGGESTION_COUNT) {
        return {
            categories: categoryRows,
            contents: contentRows
        };
    }

    categoryCount = Math.min(categoryRows.length, half);
    contentCount = Math.min(contentRows.length, SUGGESTION_COUNT - categoryCount);
    categoryCount = Math.min(categoryRows.length, SUGGESTION_COUNT - contentCount);

    return {
        categories: categoryRows.slice(0, categoryCount),
        contents: contentRows.slice(0, contentCount)
    };
}

/**
 * @param {JQuery} $list - Listing container
 * @param {string} templateSelector - Row template selector
 * @param {string} nameSelector - Name node inside the row
 * @param {Array<{name: string, url: string}>} items - Rows to show
 */
function fillList($list, templateSelector, nameSelector, items) {
    var $template = $list.children(templateSelector).first();

    $list.children(templateSelector).not($template).remove();
    resetTemplate($template);

    (items || []).forEach(function (item) {
        var $row = $template.clone();
        $row.attr('href', item.url || '#');
        $row.find(nameSelector).text(item.name || '');
        $row.removeClass('d-none').addClass('d-flex');
        $list.append($row);
    });
}

/**
 * Removes cloned suggestion rows and the query product tiles.
 */
function clearTypedResults() {
    var $panel = $(PANEL_SELECTOR);
    var $list = $panel.find('.js-nav-search-listing-results');

    fillList($list, '.js-nav-search-results-category-item', '.js-nav-search-listing-categories-item-name', []);
    fillList($list, '.js-nav-search-results-content-item', '.js-nav-search-results-content-item-name', []);
    $panel.find('.js-nav-search-query-products').empty();
    noResultQuery = '';
    loadedQuery = '';
}

/**
 * @param {string} html - Search-ShowAjax tile markup
 * @returns {boolean} True when the markup contains a product tile
 */
function hasProductTiles(html) {
    return String(html || '').indexOf('nav-search__tile') !== -1;
}

/**
 * No product tiles: popular products stay in the product column,
 * and the content column shows the no-results copy then the tags.
 *
 * @param {string} query - Trimmed query that matched nothing
 */
function showNoResults(query) {
    var $title = $(PANEL_SELECTOR).find('.js-nav-search-no-results-title');
    var template = $title.attr('data-template') || '';

    noResultQuery = query;
    $title.text(template.replace('{0}', query));
    $(PANEL_SELECTOR).find('.js-nav-search-query-products').empty();
    applyState('no-results');
}

/**
 * @param {HTMLInputElement} input - Field whose completion hint should disappear
 */
function clearSuggestion(input) {
    if (!input || !input.form) {
        return;
    }

    var $hint = $(input.form).find('.js-search-bar-suggestion');

    completionByInput.delete(input);
    $hint.find('.js-search-bar-suggestion-typed').text(input.value);
    $hint.find('.js-search-bar-suggestion-rest').text('');
    $hint.addClass('d-none').removeClass('d-flex');
}

/**
 * @param {HTMLInputElement} input - Field that owns the completion
 * @param {string} phrase - Suggested continuation
 */
function showSuggestion(input, phrase) {
    var typed = input.value;
    var $hint = $(input.form).find('.js-search-bar-suggestion');
    var matches = phrase
        && typed
        && phrase.toLowerCase().indexOf(typed.toLowerCase()) === 0
        && phrase.length > typed.length;

    if (!matches) {
        clearSuggestion(input);
        return;
    }

    completionByInput.set(input, phrase);
    $hint.find('.js-search-bar-suggestion-typed').text(typed);
    $hint.find('.js-search-bar-suggestion-rest').text(phrase.substring(typed.length));
    $hint.removeClass('d-none').addClass('d-flex');
}

/**
 * Shows or hides the close control and the desktop page overlay.
 *
 * @param {boolean} visible - Whether the search is open
 */
function setSearchCloseVisible(visible) {
    $('.js-nav-search-close')
        .toggleClass('is-in', visible)
        .attr('aria-hidden', visible ? 'false' : 'true')
        .attr('tabindex', visible ? '0' : '-1');
    $('.js-nav-search-overlay').toggleClass('is-open', visible);
}

/**
 * Drops the display lock so the closed transform can paint before the slide.
 *
 * @param {HTMLElement} panel - Search panel
 */
function preparePanelSlide(panel) {
    if (!panel.classList.contains('d-none')) {
        return;
    }

    panel.classList.remove('d-none');
    panel.getBoundingClientRect();
}

/**
 * Keeps the open panel inside the space under the bar.
 */
function syncSearchOffset() {
    var slot = document.querySelector('.nav-search-slot');
    var top;

    if (!slot) {
        return;
    }

    top = slot.getBoundingClientRect().top;
    document.documentElement.style.setProperty('--nav-search-offset', Math.max(0, Math.round(top)) + 'px');
}

/**
 * Mobile search focus collapses the bar and the burger.
 *
 * @param {boolean} active - Whether a mobile search field is open
 */
function setSearchActive(active) {
    var on = !!active && mobileSearchQuery.matches;
    var body = document.body;

    if (on) {
        window.clearTimeout(searchCloseTimer);
        body.classList.remove('search-closing');
        body.classList.add('search-active');
        $('body').removeClass('mobile-nav-open');
        $('.nav-bar .js-nav-toggle').attr('aria-expanded', 'false');
        return;
    }

    if (!body.classList.contains('search-active')) {
        return;
    }

    // Scale the bar and the burger back to 1. The panel fades on its own.
    body.classList.add('search-closing');
    body.classList.remove('search-active');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        body.classList.remove('search-closing');
        return;
    }

    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = window.setTimeout(function () {
        var openPanel = document.querySelector(PANEL_SELECTOR);

        if (openPanel && !openPanel.classList.contains('is-open')) {
            openPanel.classList.remove('is-closing');
        }

        if (!body.classList.contains('search-active')) {
            body.classList.remove('search-closing');
        }
    }, SEARCH_MOTION_MS);
}

/**
 * Hides the panel and the mobile close control.
 */
function hidePanel() {
    var panel = $(PANEL_SELECTOR).get(0);
    var fadeOut = panel
        && panel.classList.contains('is-open')
        && mobileSearchQuery.matches
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    clearSlideWait();

    setSearchCloseVisible(false);

    if (fadeOut) {
        panel.classList.add('is-closing');
        panel.classList.remove('is-open');
        setSearchActive(false);
    } else if (panel) {
        panel.classList.remove('is-open', 'is-closing');
        setSearchActive(false);
    } else {
        setSearchActive(false);
    }

    $('.navigation').find(INPUT_SELECTOR).each(function () {
        this.setAttribute('aria-expanded', 'false');
    });
}

/**
 * @param {HTMLInputElement} input - Active search field
 */
function showPanel(input) {
    var panel = $(PANEL_SELECTOR).get(0);
    var opening = panel && !panel.classList.contains('is-open');

    setSearchActive(isSearchInput(input));

    if (panel) {
        window.clearTimeout(searchCloseTimer);
        panel.classList.remove('is-closing');
        preparePanelSlide(panel);
        syncSearchOffset();
        panel.classList.add('is-open');

        if (opening) {
            holdPageWaitForSlide(panel);
        }
    }

    input.setAttribute('aria-expanded', 'true');
    setSearchCloseVisible(true);
    window.requestAnimationFrame(syncSearchOffset);
}

/**
 * The close control inside the search form that is on screen.
 *
 * @returns {HTMLButtonElement|null} Visible reset-and-close button
 */
function visibleSearchClose() {
    return $('.nav-bar__search:visible .js-nav-search-close').get(0) || null;
}

/**
 * Clears the field that owns the close control and hides the panel.
 *
 * @param {HTMLButtonElement} button - Reset and close control
 */
function closeSearch(button) {
    var form = button.closest('form');
    var input = form ? form.querySelector(INPUT_SELECTOR) : null;

    scheduleSearch.cancel();
    abortSearches();

    if (input) {
        input.value = '';
        clearSuggestion(input);
        tokenByInput.delete(input);
    }

    hidePanel();
    ensurePanel().done(function () {
        clearTypedResults();
    });

    if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
    }
}

/**
 * @param {HTMLInputElement} input - Active search field
 */
function syncFromInput(input) {
    ensurePanel().done(function () {
        if (!isSearchInput(input)) {
            return;
        }

        applyState(currentState(input));

        if (document.activeElement === input || isSearchUi(document.activeElement)) {
            showPanel(input);
        }
    });
}

/**
 * @param {HTMLInputElement} input - Active search field
 * @param {string} query - Trimmed query
 * @param {string} token - Request id stored on the field
 */
function requestSuggestions(input, query, token) {
    var $panel = $(PANEL_SELECTOR);

    abortRequest(suggestionRequest);
    suggestionRequest = $.ajax({
        url: withQuery($panel.attr('data-suggestions-url'), query),
        method: 'GET',
        dataType: 'json'
    }).done(function (data) {
        var payload = data || {};
        var $list = $panel.find('.js-nav-search-listing-results');
        var balanced;

        if (tokenByInput.get(input) !== token || input.value.trim() !== query) {
            return;
        }

        balanced = balanceSuggestions(payload.categories, payload.contents);
        fillList(
            $list,
            '.js-nav-search-results-category-item',
            '.js-nav-search-listing-categories-item-name',
            balanced.categories
        );
        fillList(
            $list,
            '.js-nav-search-results-content-item',
            '.js-nav-search-results-content-item-name',
            balanced.contents
        );
        showSuggestion(input, payload.completion || '');
    });
}

/**
 * @param {HTMLInputElement} input - Active search field
 * @param {string} query - Trimmed query
 * @param {string} token - Request id stored on the field
 */
function requestProducts(input, query, token) {
    var $panel = $(PANEL_SELECTOR);

    abortRequest(productsRequest);
    $panel.find('.js-nav-search-products-view-all').attr('href', withQuery($panel.attr('data-search-url'), query));
    productsRequest = $.ajax({
        url: withQuery($panel.attr('data-products-url'), query),
        method: 'GET'
    }).done(function (html) {
        if (tokenByInput.get(input) !== token || input.value.trim() !== query) {
            return;
        }

        if (!hasProductTiles(html)) {
            showNoResults(query);
            return;
        }

        noResultQuery = '';
        loadedQuery = query;
        $panel.find('.js-nav-search-query-products').html(html);
        applyState('search-typed');
    }).fail(function (xhr, status) {
        if (status === 'abort' || tokenByInput.get(input) !== token || input.value.trim() !== query) {
            return;
        }

        loadedQuery = query;
        applyState('search-typed');
    });
}

var scheduleSearch = debounce(function (input) {
    var query = input.value.trim();
    var token;

    if (!isSearchInput(input) || query.length < MIN_CHARS || !$(PANEL_SELECTOR).length) {
        return;
    }

    searchSerial += 1;
    token = String(searchSerial);
    tokenByInput.set(input, token);
    requestSuggestions(input, query, token);
    requestProducts(input, query, token);
}, SEARCH_DELAY_MS);

/**
 * Writes the suggested phrase into the field and submits its search form.
 *
 * @param {HTMLInputElement} input - Field that owns the completion
 */
function launchSearch(input) {
    var phrase = input && completionByInput.get(input);
    var form = input && input.form;

    if (!phrase || !form) {
        return;
    }

    scheduleSearch.cancel();
    abortSearches();
    input.value = phrase;
    clearSuggestion(input);
    form.submit();
}

/**
 * @param {KeyboardEvent} event - Key event from the search field
 * @param {HTMLInputElement} input - Field that received Tab
 */
function acceptCompletion(event, input) {
    if (event.key !== 'Tab' || event.shiftKey || event.isComposing || !completionByInput.get(input)) {
        return;
    }

    event.preventDefault();
    launchSearch(input);
}

/**
 * @param {JQuery.Event} event - focus, input, or keydown from a search field
 */
function onSearchBarEvent(event) {
    var input = event.target;
    var query;

    if (!isSearchInput(input)) {
        return;
    }

    if (event.type === 'keydown') {
        acceptCompletion(event, input);
        return;
    }

    if (event.type === 'focus') {
        setSearchActive(true);
        setSearchCloseVisible(true);
        syncSearchOffset();

        if (input.form && $(input.form).find('.js-search-bar-suggestion-typed').text() !== input.value) {
            clearSuggestion(input);
        }
    }

    query = input.value.trim();
    syncFromInput(input);

    if (event.type !== 'input') {
        return;
    }

    clearSuggestion(input);
    abortRequest(suggestionRequest);
    suggestionRequest = null;

    if (query.length >= MIN_CHARS) {
        scheduleSearch(input);
        return;
    }

    scheduleSearch.cancel();
    abortSearches();
    ensurePanel().done(function () {
        clearTypedResults();
    });
}

/**
 * @param {JQuery.Event} event - Reset on a search form
 */
function onSearchReset(event) {
    var input = event.target.querySelector(INPUT_SELECTOR);

    if (!input) {
        return;
    }

    window.setTimeout(function () {
        clearSuggestion(input);
        scheduleSearch.cancel();
        abortSearches();
        syncFromInput(input);
        clearTypedResults();
    }, 0);
}

/**
 * Hides the panel when focus leaves the search field and the panel.
 */
function onNavigationFocusOut() {
    window.setTimeout(function () {
        if (isSearchUi(document.activeElement)) {
            return;
        }

        hidePanel();
    }, 0);
}

module.exports = function () {
    var $navigation = $('.navigation');
    var $searchBars = $navigation.find(INPUT_SELECTOR);

    console.log('js-search-bar loaded', $searchBars.length, $searchBars.toArray());

    if (!$navigation.length) {
        return;
    }

    $searchBars.each(function () {
        clearSuggestion(this);
    });

    ensurePanel();

    $navigation.on('focus input keydown', INPUT_SELECTOR, onSearchBarEvent);
    $navigation.on('mousedown', '.js-search-bar-complete', function (event) {
        event.preventDefault();
    });
    $navigation.on('click', '.js-search-bar-complete', function (event) {
        var form = this.closest('form');
        var input = form ? form.querySelector(INPUT_SELECTOR) : null;

        event.preventDefault();
        launchSearch(input);
    });
    $navigation.on('click', INPUT_SELECTOR, function () {
        var input = this;

        if (!input.form || !input.form.classList.contains('d-lg-none') || !completionByInput.get(input)) {
            return;
        }

        launchSearch(input);
    });
    $navigation.on('click', '.js-clear-last-searches', function (event) {
        event.preventDefault();
        clearLastSearches(this);
    });
    $navigation.on('click', PANEL_SELECTOR + ' a[href]', function () {
        releaseClearedPhrase(queryFromHref(this.getAttribute('href')));
    });
    $navigation.on('submit', 'form[role="search"]', function () {
        var input = this.querySelector(INPUT_SELECTOR);

        if (input) {
            releaseClearedPhrase(input.value);
        }
    });
    $navigation.on('click', '.js-nav-search-close', function (event) {
        event.preventDefault();
        closeSearch(this);
    });
    $navigation.on('mousedown', '.js-nav-search-overlay', function (event) {
        event.preventDefault();
    });
    $navigation.on('click', '.js-nav-search-overlay', function () {
        var button = visibleSearchClose();

        if (button) {
            button.click();
            return;
        }

        hidePanel();
    });
    $navigation.on('reset', 'form', onSearchReset);
    $navigation.on('focusout', onNavigationFocusOut);
    $navigation.on('mousedown', PANEL_SELECTOR, function (event) {
        event.preventDefault();
    });
    $navigation.on('transitionend', '.js-nav-mobile-bar', function (event) {
        if (event.target !== event.currentTarget || !document.body.classList.contains('search-active')) {
            return;
        }

        syncSearchOffset();
    });
    window.addEventListener('resize', function () {
        if (!mobileSearchQuery.matches) {
            setSearchActive(false);
        }

        if (document.body.classList.contains('search-active') || $(PANEL_SELECTOR).hasClass('is-open')) {
            syncSearchOffset();
        }
    });
};
